/**
 * ReviewRail — cross-mime accept/reject review surface (Phase 2.2, May 2026).
 *
 * The rail consumes a {@link DiffSource} adapter that abstracts the
 * per-mime change semantics. Today three adapters ship (in `apps/client`):
 *   - `DocxDiffSource` — wraps `DocxEditorInlineHandle` tracked-changes
 *   - `XlsxDiffSource` — wraps `XlsxEditorInlineHandle` AI cells + cell overlay
 *   - `PptxDiffSource` — wraps `PptxViewerInlineHandle` + slide bbox highlights
 *
 * The rail itself is mime-agnostic: it renders change cards, accept/reject
 * affordances, bulk actions and a collapsible chrome. Brand-system styling
 * (cream + ando-orange + hairline + lucide 1.75 + font-display semibold).
 *
 * Coexistence with `<AISummaryPanel>` (the v1 ReviewSource surface):
 *   - `<AISummaryPanel>` continues to drive the existing right-rail when
 *     the editor handle is mounted; it consumes `ReviewSource` (live
 *     per-change list synced via subscribe()).
 *   - `<ReviewRail>` is the BE-summary surface, fed by `doc_diff_payload`
 *     envelopes via the new `DiffSource` adapter. Suitable for proposals
 *     that ship as a bulk summary (e.g. document_create with N sections,
 *     or document_fork_edit with cross-cell deltas) BEFORE the per-change
 *     preview chips materialise. Either rail can be mounted, or both;
 *     they don't overlap because they read different store slices.
 *
 * Why this primitive lives in `@ando/ui/editors` (not `apps/client`):
 *   - The DiffSource contract is mime-agnostic and zero-dep on @ando/core
 *     — keeps `<ReviewRail>` reusable from any host that holds an editor
 *     handle (e.g. mobile companion, future Tauri side window).
 *   - Brand tokens already live in `@ando/ui`; co-locating with editor
 *     primitives makes the chrome ship in the same lazy-loaded chunk.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronRight, CircleCheck, PanelRightClose, PanelRightOpen, X } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";

/** Discriminator for the visual badge / left strip on each card. */
export type DiffChangeKind = "insertion" | "deletion" | "modification" | "structural";

/** Mime tag — drives icon + grouping. */
export type DiffMime = "docx" | "xlsx" | "pptx";

/** Status of a single change inside the rail. */
export type DiffChangeStatus = "pending" | "accepted" | "rejected";

/**
 * Optional mime-specific preview hints rendered inside the card.
 *
 * - `text` — plain-text excerpt (docx paragraph, pptx slide title, xlsx cell)
 * - `textBefore` — original value, rendered struck-through above `text`
 * - `cellRef` — A1-style reference for xlsx ("Foglio1!B5")
 * - `slide` — 1-indexed slide number for pptx
 * - `thumbBefore` / `thumbAfter` — PPTX side-by-side thumbnails (signed URLs)
 */
export interface ChangePreview {
  readonly text?: string;
  readonly textBefore?: string;
  readonly cellRef?: string;
  readonly slide?: number;
  readonly thumbBefore?: string;
  readonly thumbAfter?: string;
}

/** One change visible in the rail. */
export interface Change {
  readonly id: string;
  readonly kind: DiffChangeKind;
  readonly mime: DiffMime;
  readonly summary: string;
  readonly rationale?: string;
  readonly status: DiffChangeStatus;
  readonly preview?: ChangePreview;
}

/**
 * Mime-agnostic contract the rail consumes. Three adapters live in
 * `apps/client/.../diffSources.ts` (one per mime); future hosts can plug
 * their own as long as they honour the subscribe()/list() shape.
 */
export interface DiffSource {
  readonly changes: ReadonlyArray<Change>;
  scrollTo(changeId: string): Promise<void> | void;
  accept(changeId: string): Promise<void> | void;
  reject(changeId: string): Promise<void> | void;
  acceptAll(): Promise<void> | void;
  rejectAll(): Promise<void> | void;
  /**
   * Subscribe to BE WS events that mutate the change list (added,
   * removed, status changed). Returns an unsubscribe fn.
   */
  subscribe(listener: () => void): () => void;
}

export interface ReviewRailProps {
  readonly source: DiffSource | null;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  /**
   * Optional className for layout overrides. The rail is opinionated about
   * width (320px when open, 48px when collapsed) and chrome, so this is
   * for parent positioning hooks (margins, z-index).
   */
  readonly className?: string;
  /**
   * Optional callback fired whenever the user clicks "Vai" on a card.
   * Hosts use this to e.g. fire a router pivot, show a toast, or open a
   * panel — the rail itself only invokes `source.scrollTo(id)`.
   */
  readonly onJumpToChange?: (changeId: string) => void;
}

const EMPTY_CHANGES: ReadonlyArray<Change> = Object.freeze([]);

export const ReviewRail: React.FC<ReviewRailProps> = ({
  source,
  isOpen,
  onToggle,
  className,
  onJumpToChange,
}) => {
  const { i18n } = useLingui();
  // Subscribe to source changes — re-pull list on each tick. The source
  // owns dedup so we don't have to.
  const [changes, setChanges] = React.useState<ReadonlyArray<Change>>(
    source?.changes ?? EMPTY_CHANGES,
  );
  React.useEffect(() => {
    if (!source) {
      setChanges(EMPTY_CHANGES);
      return;
    }
    setChanges(source.changes);
    const unsubscribe = source.subscribe(() => {
      setChanges(source.changes);
    });
    return unsubscribe;
  }, [source]);

  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = React.useState<null | "accept" | "reject">(null);

  const pending = React.useMemo(() => changes.filter((c) => c.status === "pending"), [changes]);

  // ── Collapsed rail (48px) — show icon strip + the open-toggle button ────
  if (!isOpen) {
    return (
      <aside
        className={cn(
          "flex w-[48px] shrink-0 flex-col items-center gap-2 border-hairline border-l bg-cream-50/60 py-3",
          className,
        )}
        aria-label={i18n._(msg`Pannello revisione modifiche (chiuso)`)}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-label={i18n._(msg`Apri pannello revisione modifiche`)}
          aria-expanded={false}
          className="grid h-8 w-8 place-items-center rounded-md text-ink-3 transition-colors hover:bg-white/70 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
        >
          <PanelRightOpen className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        </button>
        {pending.length > 0 ? (
          <span
            aria-label={i18n._(msg`${pending.length} modifiche in attesa`)}
            title={i18n._(msg`${pending.length} modifiche in attesa`)}
            className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-ando-orange px-1.5 font-display font-semibold text-[10px] text-white tabular-nums"
          >
            {pending.length}
          </span>
        ) : null}
      </aside>
    );
  }

  // ── Expanded rail (320px) ────────────────────────────────────────────────
  const handleAcceptAll = async () => {
    if (!source || bulkBusy) return;
    setBulkBusy("accept");
    try {
      await source.acceptAll();
    } finally {
      setBulkBusy(null);
    }
  };
  const handleRejectAll = async () => {
    if (!source || bulkBusy) return;
    setBulkBusy("reject");
    try {
      await source.rejectAll();
    } finally {
      setBulkBusy(null);
    }
  };

  return (
    <motion.aside
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 12 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className={cn(
        "flex w-[320px] shrink-0 flex-col border-hairline border-l bg-cream",
        className,
      )}
      aria-label={i18n._(msg`Pannello revisione modifiche`)}
      data-testid="review-rail"
    >
      <RailHeader
        pendingCount={pending.length}
        totalCount={changes.length}
        onToggle={onToggle}
        onAcceptAll={pending.length > 0 ? handleAcceptAll : undefined}
        onRejectAll={pending.length > 0 ? handleRejectAll : undefined}
        bulkBusy={bulkBusy}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!source ? (
          <EmptyState reason="no-source" />
        ) : changes.length === 0 ? (
          <EmptyState reason="no-changes" />
        ) : pending.length === 0 ? (
          <CompletedState total={changes.length} />
        ) : (
          <ul className="flex flex-col gap-2 p-3">
            <AnimatePresence initial={false}>
              {changes.map((c) => (
                <ChangeCard
                  key={c.id}
                  change={c}
                  active={c.id === activeId}
                  onJump={() => {
                    setActiveId(c.id);
                    void source.scrollTo(c.id);
                    onJumpToChange?.(c.id);
                  }}
                  onAccept={() => void source.accept(c.id)}
                  onReject={() => void source.reject(c.id)}
                />
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </motion.aside>
  );
};
ReviewRail.displayName = "ReviewRail";

// ── Header ──────────────────────────────────────────────────────────────

interface RailHeaderProps {
  pendingCount: number;
  totalCount: number;
  onToggle: () => void;
  onAcceptAll?: () => void;
  onRejectAll?: () => void;
  bulkBusy: null | "accept" | "reject";
}

const RailHeader: React.FC<RailHeaderProps> = ({
  pendingCount,
  totalCount,
  onToggle,
  onAcceptAll,
  onRejectAll,
  bulkBusy,
}) => {
  const { i18n } = useLingui();
  const label =
    pendingCount === 0
      ? totalCount === 0
        ? i18n._(msg`Nessuna modifica`)
        : i18n._(msg`Tutte revisionate`)
      : pendingCount === 1
        ? i18n._(msg`1 modifica proposta`)
        : i18n._(msg`${pendingCount} modifiche proposte`);
  return (
    <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-hairline border-b px-3">
      <span className="truncate font-display font-semibold text-[12px] text-ink-1">{label}</span>
      <div className="inline-flex shrink-0 items-center gap-1">
        {onAcceptAll && onRejectAll ? (
          <>
            <button
              type="button"
              onClick={onAcceptAll}
              disabled={bulkBusy !== null}
              aria-label={i18n._(msg`Accetta tutte`)}
              title={i18n._(msg`Accetta tutte`)}
              className={cn(
                "inline-flex h-7 items-center gap-1 rounded-md border border-hairline px-2",
                "font-medium text-[11px] text-ando-orange",
                "transition-colors duration-150 hover:border-ando-orange/40 hover:bg-ando-orange-soft/40",
                "disabled:cursor-wait disabled:opacity-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-1",
              )}
            >
              <Check className="h-3 w-3" strokeWidth={2.25} aria-hidden />
              <span>{i18n._(msg`Accetta`)}</span>
            </button>
            <button
              type="button"
              onClick={onRejectAll}
              disabled={bulkBusy !== null}
              aria-label={i18n._(msg`Rifiuta tutte`)}
              title={i18n._(msg`Rifiuta tutte`)}
              className={cn(
                "inline-flex h-7 items-center gap-1 rounded-md border border-hairline px-2",
                "font-medium text-[11px] text-ink-3",
                "transition-colors duration-150 hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive",
                "disabled:cursor-wait disabled:opacity-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-1",
              )}
            >
              <X className="h-3 w-3" strokeWidth={2.25} aria-hidden />
              <span>{i18n._(msg`Rifiuta`)}</span>
            </button>
          </>
        ) : null}
        <button
          type="button"
          onClick={onToggle}
          aria-label={i18n._(msg`Chiudi pannello`)}
          aria-expanded={true}
          className="grid h-7 w-7 place-items-center rounded-md text-ink-3 transition-colors hover:bg-cream-50 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
        >
          <PanelRightClose className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </header>
  );
};

// ── Card ────────────────────────────────────────────────────────────────

interface ChangeCardProps {
  change: Change;
  active: boolean;
  onJump: () => void;
  onAccept: () => void;
  onReject: () => void;
}

const ChangeCard: React.FC<ChangeCardProps> = ({ change, active, onJump, onAccept, onReject }) => {
  const { i18n } = useLingui();
  const isPending = change.status === "pending";
  const stripeColor =
    change.kind === "insertion"
      ? "bg-emerald-500/70"
      : change.kind === "deletion"
        ? "bg-rose-500/70"
        : change.kind === "structural"
          ? "bg-indigo-500/70"
          : "bg-ando-orange/70"; // modification
  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className={cn(
        "group relative overflow-hidden rounded-md border border-hairline bg-white/70 transition-colors",
        active ? "border-ando-orange/40 bg-white" : "hover:bg-white",
        !isPending && "opacity-60",
      )}
    >
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-[3px]", stripeColor)} />
      <div className="flex flex-col gap-2 px-3 py-2.5 pl-4">
        <div className="flex items-start justify-between gap-2">
          <p className="line-clamp-2 flex-1 font-display font-semibold text-[12px] text-ink-1 leading-snug">
            {change.summary}
          </p>
          {!isPending ? (
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 font-medium text-[9.5px] uppercase tracking-[0.04em]",
                change.status === "accepted"
                  ? "bg-emerald-500/12 text-emerald-700"
                  : "bg-rose-500/12 text-rose-700",
              )}
            >
              {change.status === "accepted"
                ? i18n._(msg`Accettata`)
                : i18n._(msg`Rifiutata`)}
            </span>
          ) : null}
        </div>
        {change.preview ? <PreviewBody preview={change.preview} mime={change.mime} /> : null}
        {change.rationale ? (
          <p className="text-[11px] text-ink-3 leading-snug">{change.rationale}</p>
        ) : null}
        <div className="-mx-1 flex items-center gap-1 pt-1">
          <button
            type="button"
            onClick={onJump}
            aria-label={i18n._(msg`Vai alla modifica`)}
            className="inline-flex h-6 items-center gap-1 rounded-md px-2 font-medium text-[10.5px] text-ink-2 transition-colors hover:bg-ando-orange-soft hover:text-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40"
          >
            <ChevronRight className="h-3 w-3" strokeWidth={1.75} aria-hidden />
            {i18n._(msg`Vai`)}
          </button>
          <div className="ml-auto inline-flex items-center gap-1">
            <button
              type="button"
              onClick={onAccept}
              disabled={!isPending}
              aria-label={i18n._(msg`Accetta modifica`)}
              className={cn(
                "grid h-6 w-6 place-items-center rounded-md text-ando-orange transition-colors",
                "hover:bg-ando-orange-soft/40",
                "disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
              )}
            >
              <Check className="h-3 w-3" strokeWidth={2.25} aria-hidden />
            </button>
            <button
              type="button"
              onClick={onReject}
              disabled={!isPending}
              aria-label={i18n._(msg`Rifiuta modifica`)}
              className={cn(
                "grid h-6 w-6 place-items-center rounded-md text-ink-3 transition-colors",
                "hover:bg-destructive/10 hover:text-destructive",
                "disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink-3",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
              )}
            >
              <X className="h-3 w-3" strokeWidth={2.25} aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </motion.li>
  );
};

// ── Preview body (mime-aware) ───────────────────────────────────────────

const PreviewBody: React.FC<{ preview: ChangePreview; mime: DiffMime }> = ({ preview, mime }) => {
  const { i18n } = useLingui();
  if (mime === "pptx" && (preview.thumbBefore || preview.thumbAfter)) {
    return (
      <div className="flex items-center gap-2">
        {preview.thumbBefore ? <SlideThumb url={preview.thumbBefore} label={i18n._(msg`Prima`)} /> : null}
        {preview.thumbAfter ? <SlideThumb url={preview.thumbAfter} label={i18n._(msg`Dopo`)} /> : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {preview.cellRef ? (
        <span className="font-mono text-[10.5px] text-ink-3 tabular-nums">{preview.cellRef}</span>
      ) : null}
      {preview.slide !== undefined ? (
        <span className="text-[10.5px] text-ink-3">{i18n._(msg`Slide ${preview.slide}`)}</span>
      ) : null}
      {preview.textBefore ? (
        <span className="line-clamp-2 text-[11px] text-ink-3 line-through decoration-rose-500/60">
          {preview.textBefore}
        </span>
      ) : null}
      {preview.text ? (
        <span className="line-clamp-3 text-[11.5px] text-ink-1 leading-snug">{preview.text}</span>
      ) : null}
    </div>
  );
};

const SlideThumb: React.FC<{ url: string; label: string }> = ({ url, label }) => (
  <figure className="flex flex-col gap-1">
    <img
      src={url}
      alt={label}
      width={120}
      height={90}
      loading="lazy"
      decoding="async"
      className="h-[90px] w-[120px] rounded-sm border border-hairline bg-cream-50 object-cover"
    />
    <figcaption className="text-[9.5px] text-ink-3 uppercase tracking-[0.04em]">{label}</figcaption>
  </figure>
);

// ── Empty / completed states ────────────────────────────────────────────

const EmptyState: React.FC<{ reason: "no-source" | "no-changes" }> = ({ reason }) => {
  const { i18n } = useLingui();
  return (
  <div className="grid h-full place-items-center px-6 py-10 text-center">
    <p className="text-[12px] text-ink-3 leading-snug">
      {reason === "no-source"
        ? i18n._(msg`Apri un documento per vedere le modifiche proposte.`)
        : i18n._(
            msg`Nessuna modifica proposta. Le suggestioni di Ando appariranno qui.`,
          )}
    </p>
  </div>
  );
};

const CompletedState: React.FC<{ total: number }> = ({ total }) => {
  const { i18n } = useLingui();
  return (
  <div className="grid h-full place-items-center px-6 py-10 text-center">
    <div className="flex flex-col items-center gap-2">
      <CircleCheck className="h-6 w-6 text-ando-orange" strokeWidth={1.75} aria-hidden />
      <p className="font-display font-semibold text-[12px] text-ink-1">
        {total === 1
          ? i18n._(msg`Tutta revisionata`)
          : i18n._(msg`Tutte le modifiche revisionate`)}
      </p>
      <p className="text-[11px] text-ink-3">
        {total === 1
          ? i18n._(msg`1 modifica processata.`)
          : i18n._(msg`${total} modifiche processate.`)}
      </p>
    </div>
  </div>
  );
};

// ── Public empty-changes sentinel (re-exported for adapters) ────────────

export const EMPTY_DIFF_CHANGES: ReadonlyArray<Change> = EMPTY_CHANGES;
